import React, { useState } from "react";
import { Check, Star, Crown, ExternalLink, AlertCircle } from "lucide-react";
import WelcomeBanner from "../../components/WelcomeBanner";
import PlanBadge from "../../components/PlanBadge";
import { useAuth, apiClient, formatApiError } from "../../lib/auth";

const plans = [
  {
    id: "free",
    name: "Free",
    prices: { "3m": 0, "12m": 0 },
    desc: "Explore the platform.",
    color: "#64748B",
    badge: null,
    features: ["Basic dashboard access", "Limited preview of features"],
  },
  {
    id: "pro",
    name: "Pro",
    prices: { "3m": 199, "12m": 179 },
    desc: "For agents scaling buyer leads.",
    color: "#1D6AFF",
    badge: "Most Popular",
    badgeIcon: Star,
    features: [
      "Buyer Lead Engine",
      "AI Lead Scoring",
      "AI Campaign Optimization",
      "Promote a Listing",
      "Built-in CRM",
      "Creative Maker",
      "Real-Time Analytics",
    ],
  },
  {
    id: "elite",
    name: "Elite",
    prices: { "3m": 299, "12m": 279 },
    desc: "Top producer toolkit.",
    color: "#D97706",
    badge: "Best Value",
    badgeIcon: Crown,
    features: [
      "Everything in Pro",
      "Seller Lead Engine",
      "Seller Creative Maker",
      "Priority Support",
    ],
  },
];

export default function Plans() {
  const { user, refresh } = useAuth();
  const [showCancel, setShowCancel] = useState(false);
  const [busy, setBusy] = useState(null); // 'portal' | 'cancel' | 'resume' | <planId> | null
  const [error, setError] = useState(null);
  // Default the billing-term toggle to whatever term the user is currently on (so the card
  // matching their active subscription shows "Current plan" out of the box). Falls back to 12m.
  const currentTerm = user?.plan_id?.endsWith("_3m") ? "3m" : "12m";
  const [term, setTerm] = useState(currentTerm);

  const hasSub = !!user?.stripe_subscription_id;
  const cancelScheduled = !!user?.cancel_at_period_end;
  const periodEnd = user?.current_period_end ? new Date(user.current_period_end) : null;

  const handlePlanAction = async (planId) => {
    if (planId === "free") return;
    // Logged-in dashboard users go straight to Stripe Checkout — no detour through /signup.
    // Pass cancel_path so "Back to site" in Stripe returns to this page instead of the landing/signup page.
    setBusy(planId);
    setError(null);
    try {
      const { data } = await apiClient.post("/checkout/session", {
        plan_id: `${planId}_${term}`,
        origin_url: window.location.origin,
        cancel_path: "/dashboard/plans",
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e, "Could not start checkout."));
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    setError(null);
    try {
      const { data } = await apiClient.post("/billing/portal", {
        plan_id: "_",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e, "Could not open billing portal."));
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    setBusy("cancel");
    setError(null);
    try {
      await apiClient.post("/billing/cancel");
      await refresh();
      setShowCancel(false);
    } catch (e) {
      setError(formatApiError(e, "Could not cancel."));
    } finally {
      setBusy(null);
    }
  };

  const handleResume = async () => {
    setBusy("resume");
    setError(null);
    try {
      await apiClient.post("/billing/resume");
      await refresh();
    } catch (e) {
      setError(formatApiError(e, "Could not resume subscription."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="plans-page">
      <WelcomeBanner subtitle="Manage your subscription, upgrade plans, or view invoices." />

      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 flex flex-wrap items-center justify-between gap-4 shadow-sm">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 font-semibold">Current plan</div>
          <div className="flex items-center gap-3">
            <PlanBadge plan={user?.plan || "free"} size="lg" />
            <span className="text-sm text-slate-500" data-testid="subscription-status-text">
              {user?.plan === "free" && "Upgrade anytime."}
              {user?.plan !== "free" && cancelScheduled && periodEnd && (
                <>Cancels on <span className="font-semibold text-slate-800">{periodEnd.toLocaleDateString()}</span></>
              )}
              {user?.plan !== "free" && !cancelScheduled && periodEnd && (
                <>Renews <span className="font-semibold text-slate-800">{periodEnd.toLocaleDateString()}</span></>
              )}
              {user?.plan !== "free" && !periodEnd && "Active subscription"}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasSub && cancelScheduled && (
            <button
              data-testid="resume-subscription"
              onClick={handleResume}
              disabled={busy === "resume"}
              className="text-sm bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold rounded-xl px-4 py-2 transition-all"
            >
              {busy === "resume" ? "Resuming…" : "Resume subscription"}
            </button>
          )}
          {hasSub && (
            <button
              data-testid="manage-billing"
              onClick={openPortal}
              disabled={busy === "portal"}
              className="inline-flex items-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-xl px-4 py-2 transition-all"
            >
              {busy === "portal" ? "Opening…" : "Manage billing"}
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
          {hasSub && !cancelScheduled && (
            <button
              data-testid="cancel-subscription"
              onClick={() => setShowCancel(true)}
              className="text-sm text-slate-500 hover:text-red-500 transition-colors underline"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="plans-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-center mb-7" data-testid="billing-term-toggle">
        <div className="inline-flex p-1 bg-white border border-slate-200 rounded-full shadow-sm">
          {[
            { id: "3m",  label: "3-month plan" },
            { id: "12m", label: "12-month plan", hint: "Save up to $20/mo" },
          ].map((opt) => {
            const active = term === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`term-toggle-${opt.id}`}
                onClick={() => setTerm(opt.id)}
                className={`relative px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold rounded-full transition-all ${
                  active
                    ? "bg-[#1D6AFF] text-white shadow-[0_8px_20px_-6px_rgba(29,106,255,0.45)]"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {opt.label}
                {opt.hint && (
                  <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    active ? "bg-white/20 text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  }`}>
                    {opt.hint}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-10">
        {plans.map((p) => {
          const Icon = p.badgeIcon;
          // Match current plan against the actual plan_id (tier + term) for paid plans,
          // so toggling the term reveals "Switch" on the other card. Free stays simple.
          const isCurrent = p.id === "free"
            ? user?.plan === "free"
            : user?.plan_id === `${p.id}_${term}`;
          const price = p.prices[term] ?? 0;
          const monthlyPrice = p.prices["3m"] ?? 0;
          const showSavings = term === "12m" && p.id !== "free" && monthlyPrice > price;
          const savingsPerMonth = showSavings ? monthlyPrice - price : 0;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl p-7 bg-white border-2 transition-all shadow-sm ${
                isCurrent
                  ? "border-emerald-400/70"
                  : p.id === "pro"
                  ? "border-[#1D6AFF]/50 glow-blue"
                  : p.id === "elite"
                  ? "border-amber-400/60 glow-gold"
                  : "border-slate-200"
              }`}
            >
              {p.badge && (
                <div
                  className="absolute -top-3 left-6 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full flex items-center gap-1 shadow-md"
                  style={
                    p.id === "elite"
                      ? { background: "linear-gradient(90deg,#F5C518,#D97706)", color: "#fff" }
                      : { background: "#1D6AFF", color: "#fff" }
                  }
                >
                  {Icon && <Icon className="w-2.5 h-2.5" />}
                  {p.badge}
                </div>
              )}
              <div className="font-display text-xl font-semibold mb-1" style={{ color: p.id === "elite" ? "#B45309" : "#0F172A" }}>{p.name}</div>
              <div className="text-xs text-slate-500 mb-4">{p.desc}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-4xl font-bold text-slate-900">${price}</span>
                <span className="text-slate-500 text-sm">/month</span>
              </div>
              <div className="h-5 mb-4 flex items-center gap-2 text-[11px]">
                {p.id !== "free" && (
                  <span className="text-slate-400">
                    billed monthly · {term === "12m" ? "12-month plan" : "3-month plan"}
                  </span>
                )}
                {showSavings && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold">
                    Save ${savingsPerMonth}/mo
                  </span>
                )}
              </div>
              <button
                data-testid={`plan-action-${p.id}`}
                disabled={isCurrent || busy === p.id}
                onClick={() => handlePlanAction(p.id)}
                className={`w-full rounded-xl py-2.5 font-semibold text-sm transition-all mb-5 ${
                  isCurrent
                    ? "bg-emerald-50 border border-emerald-300 text-emerald-700 cursor-default"
                    : p.id === "elite"
                    ? "bg-gradient-to-r from-amber-400 to-amber-600 text-white hover:from-amber-500 hover:to-amber-700 disabled:opacity-60"
                    : p.id === "pro"
                    ? "bg-[#1D6AFF] hover:bg-[#1552C6] text-white disabled:opacity-60"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
                }`}
              >
                {isCurrent
                  ? "Current plan"
                  : busy === p.id
                  ? "Redirecting…"
                  : user?.plan === "elite" && p.id !== "elite"
                  ? "Downgrade"
                  : user?.plan === "free" || (user?.plan === "pro" && p.id === "elite")
                  ? "Upgrade"
                  : "Switch"}
              </button>
              <ul className="space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-700">
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: p.color }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h2 className="font-display text-xl font-semibold text-slate-900">Billing history</h2>
          {hasSub && (
            <button
              data-testid="open-portal-invoices"
              onClick={openPortal}
              disabled={busy === "portal"}
              className="inline-flex items-center gap-1.5 text-sm text-[#1D6AFF] hover:text-[#1552C6] font-semibold"
            >
              View invoices & receipts <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <p className="text-sm text-slate-500 leading-relaxed">
          {hasSub
            ? "Your invoices, receipts, and saved payment methods are managed by Stripe. Click \"Manage billing\" or \"View invoices & receipts\" above to access them; you'll be redirected to a secure Stripe portal."
            : "No invoices yet. Upgrade to Pro or Elite to start your journey; your billing history will appear here once you're subscribed."}
        </p>
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cancel-modal">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setShowCancel(false)} />
          <div className="relative bg-white border border-slate-200 rounded-3xl p-8 max-w-md w-full shadow-[0_30px_80px_-20px_rgba(15,23,42,0.25)]">
            <h3 className="font-display text-2xl font-bold mb-3 text-slate-900">Cancel subscription?</h3>
            <p className="text-slate-500 mb-6 text-sm leading-relaxed">
              You'll keep full access until {periodEnd ? periodEnd.toLocaleDateString() : "the end of your current billing cycle"}.
              After that, your plan will drop to free and recurring charges will stop. You can resume anytime before then.
            </p>
            {error && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCancel(false); setError(null); }}
                disabled={busy === "cancel"}
                className="flex-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 rounded-xl py-3 font-semibold transition-all disabled:opacity-60"
                data-testid="cancel-keep"
              >
                Keep plan
              </button>
              <button
                data-testid="cancel-confirm"
                onClick={handleCancel}
                disabled={busy === "cancel"}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white rounded-xl py-3 font-semibold transition-all"
              >
                {busy === "cancel" ? "Cancelling…" : "Confirm cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
